
import { SQLModule } from '../types';

// Additional modules for SQL Intermediate course
export const SQL_INTERMEDIATE_EXPANDED_MODULES: SQLModule[] = [
  {
    id: 'sql-intermediate-3',
    title: 'Advanced WHERE and Conditional Logic',
    description: 'Master complex conditional logic, subqueries in WHERE clauses, and advanced filtering techniques.',
    content: `# Advanced WHERE and Conditional Logic

## Complex WHERE Conditions

### Multiple Conditions with AND/OR

\`\`\`sql
-- Complex logical conditions
SELECT customer_id, first_name, last_name, created_at, total_spent
FROM customers
WHERE (
    (created_at >= '2024-01-01' AND total_spent > 1000) 
    OR 
    (created_at >= '2023-01-01' AND total_spent > 5000)
) AND status = 'active';

-- Using parentheses for proper precedence
SELECT product_name, price, category, stock_quantity
FROM products
WHERE (category = 'Electronics' OR category = 'Computers')
    AND (price BETWEEN 100 AND 1000)
    AND (stock_quantity > 0 OR is_preorder = TRUE);
\`\`\`

### Advanced Pattern Matching

\`\`\`sql
-- Advanced LIKE patterns
SELECT customer_id, email, phone
FROM customers
WHERE 
    -- Email ends with specific domains
    (email LIKE '%.edu' OR email LIKE '%.org')
    -- Phone has specific area codes
    AND (phone LIKE '(555)%' OR phone LIKE '(444)%' OR phone IS NULL)
    -- Name patterns
    AND (first_name LIKE 'A%' OR last_name LIKE '%son');

-- Using REGEXP for complex patterns (MySQL)
SELECT product_name, sku, description
FROM products
WHERE 
    -- SKU follows pattern: 3 letters, dash, 4 numbers
    sku REGEXP '^[A-Z]{3}-[0-9]{4}$'
    -- Description contains phone number pattern
    OR description REGEXP '[0-9]{3}-[0-9]{3}-[0-9]{4}';
\`\`\`

### Range and List Conditions

\`\`\`sql
-- Multiple range conditions
SELECT order_id, customer_id, order_date, total_amount
FROM orders
WHERE 
    -- Date ranges
    order_date BETWEEN '2024-01-01' AND '2024-03-31'
    -- Amount ranges with multiple conditions
    AND (
        (total_amount BETWEEN 50 AND 200 AND shipping_method = 'standard')
        OR 
        (total_amount > 200 AND shipping_method IN ('express', 'overnight'))
    )
    -- Status not in specific list
    AND order_status NOT IN ('cancelled', 'refunded', 'disputed');

-- Using CASE in WHERE clause
SELECT customer_id, first_name, last_name, account_type, balance
FROM customers
WHERE 
    CASE account_type
        WHEN 'premium' THEN balance > 1000
        WHEN 'gold' THEN balance > 5000
        WHEN 'platinum' THEN balance > 10000
        ELSE balance > 0
    END;
\`\`\`

## Subqueries in WHERE Clauses

### Existence Subqueries

\`\`\`sql
-- Find customers who have placed orders
SELECT customer_id, first_name, last_name, email
FROM customers c
WHERE EXISTS (
    SELECT 1 FROM orders o 
    WHERE o.customer_id = c.customer_id 
    AND o.order_status = 'completed'
);

-- Find customers who have NOT placed any orders
SELECT customer_id, first_name, last_name, email, created_at
FROM customers c
WHERE NOT EXISTS (
    SELECT 1 FROM orders o 
    WHERE o.customer_id = c.customer_id
);

-- Find products that have never been ordered
SELECT product_id, product_name, price, stock_quantity
FROM products p
WHERE NOT EXISTS (
    SELECT 1 FROM order_items oi 
    WHERE oi.product_id = p.product_id
);
\`\`\`

### Comparison Subqueries

\`\`\`sql
-- Customers with above-average order amounts
SELECT customer_id, first_name, last_name
FROM customers c
WHERE (
    SELECT AVG(total_amount) 
    FROM orders o 
    WHERE o.customer_id = c.customer_id
) > (
    SELECT AVG(total_amount) FROM orders
);

-- Products priced higher than category average
SELECT product_id, product_name, price, category_id
FROM products p1
WHERE price > (
    SELECT AVG(price) 
    FROM products p2 
    WHERE p2.category_id = p1.category_id
);

-- Find customers with more orders than the median
SELECT customer_id, first_name, last_name,
    (SELECT COUNT(*) FROM orders o WHERE o.customer_id = c.customer_id) as order_count
FROM customers c
WHERE (
    SELECT COUNT(*) FROM orders o WHERE o.customer_id = c.customer_id
) > (
    SELECT PERCENTILE_CONT(0.5) WITHIN GROUP (ORDER BY order_count)
    FROM (
        SELECT COUNT(*) as order_count 
        FROM orders 
        GROUP BY customer_id
    ) sub
);
\`\`\`

### IN/NOT IN Subqueries

\`\`\`sql
-- Customers who ordered specific products
SELECT customer_id, first_name, last_name
FROM customers
WHERE customer_id IN (
    SELECT DISTINCT o.customer_id
    FROM orders o
    JOIN order_items oi ON o.order_id = oi.order_id
    JOIN products p ON oi.product_id = p.product_id
    WHERE p.category_id IN (1, 2, 3)  -- Electronics categories
    AND o.order_date >= '2024-01-01'
);

-- Products not ordered in the last 6 months
SELECT product_id, product_name, price, last_ordered_date
FROM products
WHERE product_id NOT IN (
    SELECT DISTINCT oi.product_id
    FROM order_items oi
    JOIN orders o ON oi.order_id = o.order_id
    WHERE o.order_date >= DATE_SUB(NOW(), INTERVAL 6 MONTH)
    AND oi.product_id IS NOT NULL  -- Important: avoid NULL issues
);
\`\`\`

## Advanced Conditional Logic

### Nested CASE Statements

\`\`\`sql
-- Complex customer segmentation
SELECT 
    customer_id,
    first_name,
    last_name,
    total_spent,
    order_count,
    last_order_date,
    CASE 
        WHEN total_spent > 10000 THEN
            CASE 
                WHEN order_count > 50 THEN 'VIP Frequent'
                WHEN DATEDIFF(NOW(), last_order_date) <= 30 THEN 'VIP Recent'
                ELSE 'VIP Inactive'
            END
        WHEN total_spent > 5000 THEN
            CASE 
                WHEN order_count > 20 THEN 'Premium Frequent'
                WHEN DATEDIFF(NOW(), last_order_date) <= 60 THEN 'Premium Active'
                ELSE 'Premium Inactive'
            END
        WHEN total_spent > 1000 THEN
            CASE 
                WHEN DATEDIFF(NOW(), last_order_date) <= 90 THEN 'Regular Active'
                ELSE 'Regular Inactive'
            END
        ELSE 'New Customer'
    END as customer_segment
FROM (
    SELECT 
        c.customer_id,
        c.first_name,
        c.last_name,
        COALESCE(SUM(o.total_amount), 0) as total_spent,
        COUNT(o.order_id) as order_count,
        MAX(o.order_date) as last_order_date
    FROM customers c
    LEFT JOIN orders o ON c.customer_id = o.customer_id
    WHERE c.is_active = TRUE
    GROUP BY c.customer_id, c.first_name, c.last_name
) customer_stats;
\`\`\`

### Multiple Table Conditional Logic

\`\`\`sql
-- Advanced product recommendations based on multiple factors
SELECT 
    p.product_id,
    p.product_name,
    p.price,
    c.category_name,
    CASE 
        WHEN p.stock_quantity = 0 THEN 'Out of Stock'
        WHEN p.stock_quantity < 10 THEN 'Low Stock'
        WHEN p.stock_quantity < 50 THEN 'Normal Stock'
        ELSE 'High Stock'
    END as stock_status,
    CASE 
        WHEN trending.trend_score > 0.8 AND p.price < avg_prices.avg_price THEN 'Hot Deal'
        WHEN trending.trend_score > 0.6 THEN 'Trending'
        WHEN p.rating > 4.5 AND p.review_count > 100 THEN 'Top Rated'
        WHEN p.created_at > DATE_SUB(NOW(), INTERVAL 30 DAY) THEN 'New Arrival'
        WHEN discount.discount_percent > 20 THEN 'On Sale'
        ELSE 'Regular'
    END as product_label
FROM products p
JOIN categories c ON p.category_id = c.category_id
LEFT JOIN (
    SELECT 
        product_id,
        COUNT(*) / (SELECT COUNT(*) FROM order_items) as trend_score
    FROM order_items oi
    JOIN orders o ON oi.order_id = o.order_id
    WHERE o.order_date >= DATE_SUB(NOW(), INTERVAL 30 DAY)
    GROUP BY product_id
) trending ON p.product_id = trending.product_id
LEFT JOIN (
    SELECT 
        category_id,
        AVG(price) as avg_price
    FROM products
    GROUP BY category_id
) avg_prices ON p.category_id = avg_prices.category_id
LEFT JOIN (
    SELECT 
        product_id,
        CASE 
            WHEN original_price > 0 THEN ((original_price - price) / original_price) * 100
            ELSE 0
        END as discount_percent
    FROM products
) discount ON p.product_id = discount.product_id
WHERE p.is_active = TRUE
ORDER BY 
    CASE 
        WHEN trending.trend_score > 0.8 AND p.price < avg_prices.avg_price THEN 1
        WHEN trending.trend_score > 0.6 THEN 2
        WHEN p.rating > 4.5 AND p.review_count > 100 THEN 3
        ELSE 4
    END,
    p.product_name;
\`\`\`

## Date and Time Conditional Logic

### Time-based Conditions

\`\`\`sql
-- Business hours and seasonal analysis
SELECT 
    order_id,
    customer_id,
    order_date,
    HOUR(order_date) as order_hour,
    DAYOFWEEK(order_date) as day_of_week,
    total_amount,
    CASE 
        WHEN HOUR(order_date) BETWEEN 9 AND 17 
            AND DAYOFWEEK(order_date) BETWEEN 2 AND 6 
            THEN 'Business Hours'
        WHEN DAYOFWEEK(order_date) IN (1, 7) 
            THEN 'Weekend'
        ELSE 'After Hours'
    END as time_category,
    CASE 
        WHEN MONTH(order_date) IN (12, 1, 2) THEN 'Winter'
        WHEN MONTH(order_date) IN (3, 4, 5) THEN 'Spring'
        WHEN MONTH(order_date) IN (6, 7, 8) THEN 'Summer'
        WHEN MONTH(order_date) IN (9, 10, 11) THEN 'Fall'
    END as season,
    CASE 
        WHEN MONTH(order_date) = 12 AND DAY(order_date) BETWEEN 20 AND 31 THEN 'Holiday Season'
        WHEN MONTH(order_date) = 11 AND DAY(order_date) BETWEEN 20 AND 30 THEN 'Black Friday Period'
        WHEN MONTH(order_date) = 2 AND DAY(order_date) = 14 THEN 'Valentine Day'
        ELSE 'Regular Period'
    END as special_period
FROM orders
WHERE order_date >= '2024-01-01'
    AND (
        -- Weekend orders over $100
        (DAYOFWEEK(order_date) IN (1, 7) AND total_amount > 100)
        OR
        -- Weekday orders during business hours
        (DAYOFWEEK(order_date) BETWEEN 2 AND 6 
         AND HOUR(order_date) BETWEEN 9 AND 17)
        OR
        -- Holiday season orders
        (MONTH(order_date) = 12 AND DAY(order_date) BETWEEN 20 AND 31)
    );
\`\`\`

### Dynamic Date Ranges

\`\`\`sql
-- Dynamic reporting periods
SELECT 
    customer_id,
    first_name,
    last_name,
    COUNT(o.order_id) as total_orders,
    SUM(o.total_amount) as total_spent,
    AVG(o.total_amount) as avg_order_value
FROM customers c
LEFT JOIN orders o ON c.customer_id = o.customer_id
WHERE c.created_at <= DATE_SUB(NOW(), INTERVAL 30 DAY)  -- Exclude very new customers
    AND (
        o.order_date IS NULL  -- Include customers with no orders
        OR 
        o.order_date BETWEEN 
            CASE 
                WHEN DAYOFWEEK(NOW()) = 2 THEN DATE_SUB(NOW(), INTERVAL 7 DAY)  -- Monday: last week
                WHEN DAYOFWEEK(NOW()) = 1 THEN DATE_SUB(NOW(), INTERVAL 6 DAY)  -- Sunday: last week
                ELSE DATE_SUB(NOW(), INTERVAL DAYOFWEEK(NOW()) - 2 DAY)  -- This week
            END
            AND NOW()
    )
GROUP BY c.customer_id, c.first_name, c.last_name
HAVING 
    total_orders > 0 
    OR 
    c.customer_id IN (
        SELECT customer_id FROM customers 
        WHERE created_at >= DATE_SUB(NOW(), INTERVAL 7 DAY)
    )
ORDER BY total_spent DESC NULLS LAST;
\`\`\``,
    duration: '55 min',
    estimatedTime: 55,
    difficulty: 'intermediate',
    order: 3
  },
  {
    id: 'sql-intermediate-4',
    title: 'Subqueries and Set Operations',
    description: 'Master correlated subqueries, derived tables, and set operations like UNION, INTERSECT, and EXCEPT.',
    content: `# Subqueries and Set Operations

## Types of Subqueries

### Scalar Subqueries

\`\`\`sql
-- Scalar subquery returns a single value
SELECT 
    customer_id,
    first_name,
    last_name,
    (SELECT COUNT(*) FROM orders o WHERE o.customer_id = c.customer_id) as order_count,
    (SELECT MAX(total_amount) FROM orders o WHERE o.customer_id = c.customer_id) as highest_order,
    (SELECT AVG(total_amount) FROM orders o WHERE o.customer_id = c.customer_id) as avg_order_value
FROM customers c
WHERE c.is_active = TRUE;

-- Using scalar subqueries in calculations
SELECT 
    product_id,
    product_name,
    price,
    (SELECT AVG(price) FROM products) as market_avg_price,
    price - (SELECT AVG(price) FROM products) as price_difference,
    CASE 
        WHEN price > (SELECT AVG(price) FROM products) THEN 'Above Average'
        WHEN price < (SELECT AVG(price) FROM products) THEN 'Below Average'
        ELSE 'Average'
    END as price_category
FROM products
WHERE is_active = TRUE;
\`\`\`

### Correlated Subqueries

\`\`\`sql
-- Correlated subquery references outer query
SELECT 
    p1.product_id,
    p1.product_name,
    p1.category_id,
    p1.price,
    (
        SELECT COUNT(*) 
        FROM products p2 
        WHERE p2.category_id = p1.category_id 
        AND p2.price < p1.price
    ) as products_cheaper_in_category,
    (
        SELECT AVG(rating) 
        FROM reviews r 
        WHERE r.product_id = p1.product_id
    ) as avg_rating
FROM products p1
WHERE p1.is_active = TRUE
ORDER BY p1.category_id, p1.price;

-- Find customers with above-average spending in their region
SELECT 
    c.customer_id,
    c.first_name,
    c.last_name,
    c.city,
    c.state,
    (
        SELECT SUM(total_amount) 
        FROM orders o 
        WHERE o.customer_id = c.customer_id
    ) as total_spent
FROM customers c
WHERE (
    SELECT SUM(total_amount) 
    FROM orders o 
    WHERE o.customer_id = c.customer_id
) > (
    SELECT AVG(customer_total) 
    FROM (
        SELECT SUM(total_amount) as customer_total
        FROM orders o2
        JOIN customers c2 ON o2.customer_id = c2.customer_id
        WHERE c2.state = c.state
        GROUP BY c2.customer_id
    ) regional_averages
);
\`\`\`

### Table Subqueries (Derived Tables)

\`\`\`sql
-- Using subquery as a table (derived table)
SELECT 
    monthly_sales.month,
    monthly_sales.year,
    monthly_sales.total_sales,
    monthly_sales.order_count,
    monthly_sales.avg_order_value,
    CASE 
        WHEN monthly_sales.total_sales > overall_avg.avg_monthly_sales THEN 'Above Average'
        ELSE 'Below Average'
    END as performance
FROM (
    SELECT 
        MONTH(order_date) as month,
        YEAR(order_date) as year,
        SUM(total_amount) as total_sales,
        COUNT(*) as order_count,
        AVG(total_amount) as avg_order_value
    FROM orders
    WHERE order_status = 'completed'
    GROUP BY YEAR(order_date), MONTH(order_date)
) monthly_sales
CROSS JOIN (
    SELECT AVG(monthly_total) as avg_monthly_sales
    FROM (
        SELECT SUM(total_amount) as monthly_total
        FROM orders
        WHERE order_status = 'completed'
        GROUP BY YEAR(order_date), MONTH(order_date)
    ) all_months
) overall_avg
ORDER BY monthly_sales.year DESC, monthly_sales.month DESC;
\`\`\`

### Multiple Level Subqueries

\`\`\`sql
-- Nested subqueries with multiple levels
SELECT 
    category_name,
    product_count,
    avg_price,
    top_product_name,
    top_product_price
FROM (
    SELECT 
        c.category_name,
        COUNT(p.product_id) as product_count,
        AVG(p.price) as avg_price,
        (
            SELECT product_name 
            FROM products p2 
            WHERE p2.category_id = c.category_id 
            ORDER BY (
                SELECT COUNT(*) 
                FROM order_items oi 
                WHERE oi.product_id = p2.product_id
            ) DESC 
            LIMIT 1
        ) as top_product_name,
        (
            SELECT price 
            FROM products p3 
            WHERE p3.category_id = c.category_id 
            ORDER BY (
                SELECT COUNT(*) 
                FROM order_items oi 
                WHERE oi.product_id = p3.product_id
            ) DESC 
            LIMIT 1
        ) as top_product_price
    FROM categories c
    JOIN products p ON c.category_id = p.category_id
    WHERE p.is_active = TRUE
    GROUP BY c.category_id, c.category_name
) category_stats
WHERE product_count > 5
ORDER BY avg_price DESC;
\`\`\`

## Set Operations

### UNION - Combining Results

\`\`\`sql
-- UNION removes duplicates
SELECT customer_id, first_name, last_name, 'High Value' as customer_type
FROM customers
WHERE customer_id IN (
    SELECT customer_id 
    FROM orders 
    GROUP BY customer_id 
    HAVING SUM(total_amount) > 5000
)

UNION

SELECT customer_id, first_name, last_name, 'Frequent Buyer' as customer_type
FROM customers
WHERE customer_id IN (
    SELECT customer_id 
    FROM orders 
    GROUP BY customer_id 
    HAVING COUNT(*) > 20
)

ORDER BY customer_type, last_name;

-- UNION ALL keeps all rows including duplicates
SELECT product_id, product_name, 'Best Seller' as category
FROM products
WHERE product_id IN (
    SELECT product_id 
    FROM order_items 
    GROUP BY product_id 
    ORDER BY SUM(quantity) DESC 
    LIMIT 10
)

UNION ALL

SELECT product_id, product_name, 'Highly Rated' as category
FROM products
WHERE product_id IN (
    SELECT product_id 
    FROM reviews 
    GROUP BY product_id 
    HAVING AVG(rating) >= 4.5 AND COUNT(*) >= 20
)

ORDER BY category, product_name;
\`\`\`

### Complex UNION Scenarios

\`\`\`sql
-- Comprehensive customer activity report
SELECT 
    customer_id,
    first_name,
    last_name,
    email,
    'Recent Customer' as status,
    created_at as relevant_date,
    NULL as order_amount
FROM customers
WHERE created_at >= DATE_SUB(NOW(), INTERVAL 30 DAY)

UNION ALL

SELECT 
    c.customer_id,
    c.first_name,
    c.last_name,
    c.email,
    'Big Spender' as status,
    o.order_date as relevant_date,
    o.total_amount as order_amount
FROM customers c
JOIN orders o ON c.customer_id = o.customer_id
WHERE o.total_amount > 1000
    AND o.order_date >= DATE_SUB(NOW(), INTERVAL 90 DAY)

UNION ALL

SELECT 
    c.customer_id,
    c.first_name,
    c.last_name,
    c.email,
    'Inactive Customer' as status,
    MAX(o.order_date) as relevant_date,
    NULL as order_amount
FROM customers c
LEFT JOIN orders o ON c.customer_id = o.customer_id
GROUP BY c.customer_id, c.first_name, c.last_name, c.email
HAVING MAX(o.order_date) < DATE_SUB(NOW(), INTERVAL 180 DAY)
    OR MAX(o.order_date) IS NULL

ORDER BY status, relevant_date DESC;
\`\`\`

### INTERSECT and EXCEPT (MySQL doesn't support, but we can simulate)

\`\`\`sql
-- Simulate INTERSECT - customers who are both high-value AND frequent
SELECT DISTINCT c1.customer_id, c1.first_name, c1.last_name
FROM (
    SELECT customer_id, first_name, last_name
    FROM customers
    WHERE customer_id IN (
        SELECT customer_id 
        FROM orders 
        GROUP BY customer_id 
        HAVING SUM(total_amount) > 5000
    )
) c1
INNER JOIN (
    SELECT customer_id, first_name, last_name
    FROM customers
    WHERE customer_id IN (
        SELECT customer_id 
        FROM orders 
        GROUP BY customer_id 
        HAVING COUNT(*) > 20
    )
) c2 ON c1.customer_id = c2.customer_id;

-- Simulate EXCEPT - high-value customers who are NOT frequent buyers
SELECT customer_id, first_name, last_name
FROM customers
WHERE customer_id IN (
    SELECT customer_id 
    FROM orders 
    GROUP BY customer_id 
    HAVING SUM(total_amount) > 5000
)
AND customer_id NOT IN (
    SELECT customer_id 
    FROM orders 
    GROUP BY customer_id 
    HAVING COUNT(*) > 20
);
\`\`\`

## Advanced Subquery Patterns

### Ranking and Top-N Queries

\`\`\`sql
-- Top 3 products in each category by sales
SELECT 
    category_name,
    product_name,
    total_sold,
    category_rank
FROM (
    SELECT 
        c.category_name,
        p.product_name,
        SUM(oi.quantity) as total_sold,
        @rank := CASE 
            WHEN @prev_category = c.category_name THEN @rank + 1 
            ELSE 1 
        END as category_rank,
        @prev_category := c.category_name
    FROM products p
    JOIN categories c ON p.category_id = c.category_id
    JOIN order_items oi ON p.product_id = oi.product_id
    JOIN orders o ON oi.order_id = o.order_id
    CROSS JOIN (SELECT @rank := 0, @prev_category := '') r
    WHERE o.order_status = 'completed'
    GROUP BY c.category_id, c.category_name, p.product_id, p.product_name
    ORDER BY c.category_name, total_sold DESC
) ranked_products
WHERE category_rank <= 3;

-- Alternative using correlated subquery for ranking
SELECT 
    c.category_name,
    p.product_name,
    sales_data.total_sold
FROM categories c
JOIN products p ON c.category_id = p.category_id
JOIN (
    SELECT 
        product_id,
        SUM(quantity) as total_sold
    FROM order_items oi
    JOIN orders o ON oi.order_id = o.order_id
    WHERE o.order_status = 'completed'
    GROUP BY product_id
) sales_data ON p.product_id = sales_data.product_id
WHERE (
    SELECT COUNT(*)
    FROM products p2
    JOIN (
        SELECT 
            product_id,
            SUM(quantity) as total_sold
        FROM order_items oi2
        JOIN orders o2 ON oi2.order_id = o2.order_id
        WHERE o2.order_status = 'completed'
        GROUP BY product_id
    ) sales_data2 ON p2.product_id = sales_data2.product_id
    WHERE p2.category_id = p.category_id
    AND sales_data2.total_sold > sales_data.total_sold
) < 3
ORDER BY c.category_name, sales_data.total_sold DESC;
\`\`\`

### Conditional Aggregation with Subqueries

\`\`\`sql
-- Customer segmentation with conditional metrics
SELECT 
    customer_id,
    first_name,
    last_name,
    total_orders,
    total_spent,
    avg_order_value,
    months_as_customer,
    CASE 
        WHEN total_spent > (SELECT PERCENTILE_CONT(0.9) WITHIN GROUP (ORDER BY customer_total) FROM customer_totals) THEN 'VIP'
        WHEN total_spent > (SELECT PERCENTILE_CONT(0.7) WITHIN GROUP (ORDER BY customer_total) FROM customer_totals) THEN 'Premium'
        WHEN total_spent > (SELECT PERCENTILE_CONT(0.3) WITHIN GROUP (ORDER BY customer_total) FROM customer_totals) THEN 'Standard'
        ELSE 'Basic'
    END as customer_tier
FROM (
    SELECT 
        c.customer_id,
        c.first_name,
        c.last_name,
        COUNT(o.order_id) as total_orders,
        COALESCE(SUM(o.total_amount), 0) as total_spent,
        COALESCE(AVG(o.total_amount), 0) as avg_order_value,
        TIMESTAMPDIFF(MONTH, c.created_at, NOW()) as months_as_customer
    FROM customers c
    LEFT JOIN orders o ON c.customer_id = o.customer_id AND o.order_status = 'completed'
    WHERE c.is_active = TRUE
    GROUP BY c.customer_id, c.first_name, c.last_name, c.created_at
) customer_metrics
CROSS JOIN (
    SELECT 
        SUM(o.total_amount) as customer_total
    FROM customers c
    LEFT JOIN orders o ON c.customer_id = o.customer_id AND o.order_status = 'completed'
    WHERE c.is_active = TRUE
    GROUP BY c.customer_id
) customer_totals
ORDER BY total_spent DESC;
\`\`\``,
    duration: '60 min',
    estimatedTime: 60,
    difficulty: 'intermediate',
    order: 4
  },
  {
    id: 'sql-intermediate-5',
    title: 'Date/Time Functions and String Manipulation',
    description: 'Master advanced date/time operations and string manipulation techniques for data processing.',
    content: `# Advanced Date/Time and String Functions

## Advanced Date/Time Operations

### Date Arithmetic and Intervals

\`\`\`sql
-- Complex date calculations
SELECT 
    customer_id,
    first_name,
    last_name,
    created_at,
    -- Various date additions
    DATE_ADD(created_at, INTERVAL 1 YEAR) as anniversary_date,
    DATE_ADD(created_at, INTERVAL 6 MONTH) as six_month_mark,
    DATE_ADD(created_at, INTERVAL 90 DAY) as ninety_day_mark,
    
    -- Date subtractions
    DATE_SUB(NOW(), INTERVAL 30 DAY) as thirty_days_ago,
    DATE_SUB(created_at, INTERVAL 1 WEEK) as week_before_signup,
    
    -- Complex intervals
    DATE_ADD(created_at, INTERVAL '1-6' YEAR_MONTH) as one_year_six_months,
    DATE_ADD(created_at, INTERVAL '2 3:30:45' DAY_SECOND) as complex_interval,
    
    -- Time differences
    DATEDIFF(NOW(), created_at) as days_as_customer,
    TIMESTAMPDIFF(MONTH, created_at, NOW()) as months_as_customer,
    TIMESTAMPDIFF(HOUR, created_at, NOW()) as hours_as_customer
FROM customers
WHERE created_at >= '2023-01-01';

-- Working days calculation (excluding weekends)
SELECT 
    order_id,
    order_date,
    shipped_date,
    DATEDIFF(shipped_date, order_date) as total_days,
    DATEDIFF(shipped_date, order_date) - 
    (2 * FLOOR(DATEDIFF(shipped_date, order_date) / 7)) -
    CASE WHEN DAYOFWEEK(order_date) = 1 THEN 1 ELSE 0 END -
    CASE WHEN DAYOFWEEK(shipped_date) = 7 THEN 1 ELSE 0 END as working_days
FROM orders
WHERE shipped_date IS NOT NULL;
\`\`\`

### Date Formatting and Parsing

\`\`\`sql
-- Advanced date formatting
SELECT 
    order_date,
    -- Standard formats
    DATE_FORMAT(order_date, '%Y-%m-%d') as iso_date,
    DATE_FORMAT(order_date, '%d/%m/%Y') as european_date,
    DATE_FORMAT(order_date, '%m/%d/%Y') as american_date,
    
    -- Custom formats
    DATE_FORMAT(order_date, '%W, %M %D, %Y') as full_date,
    DATE_FORMAT(order_date, '%a %b %e, %Y at %l:%i %p') as readable_datetime,
    DATE_FORMAT(order_date, 'Q%q %Y') as quarter_year,
    
    -- Time formatting
    TIME_FORMAT(order_date, '%H:%i:%s') as time_24hr,
    TIME_FORMAT(order_date, '%h:%i %p') as time_12hr,
    
    -- Custom business formats
    CONCAT(
        DATE_FORMAT(order_date, '%Y'),
        '-W',
        LPAD(WEEK(order_date, 1), 2, '0')
    ) as iso_week,
    
    CONCAT(
        'FY',
        CASE 
            WHEN MONTH(order_date) >= 4 THEN YEAR(order_date)
            ELSE YEAR(order_date) - 1
        END
    ) as fiscal_year
FROM orders
WHERE order_date >= '2024-01-01';

-- String to date conversion
SELECT 
    -- Various string formats to dates
    STR_TO_DATE('2024-03-15 14:30:00', '%Y-%m-%d %H:%i:%s') as from_iso,
    STR_TO_DATE('15/03/2024', '%d/%m/%Y') as from_european,
    STR_TO_DATE('March 15, 2024', '%M %d, %Y') as from_text,
    STR_TO_DATE('2024Q1', '%YQ%q') as from_quarter,
    
    -- Unix timestamp conversion
    FROM_UNIXTIME(1710504600) as from_unix,
    UNIX_TIMESTAMP('2024-03-15 14:30:00') as to_unix;
\`\`\`

### Time Zone Handling

\`\`\`sql
-- Time zone conversions
SELECT 
    order_date,
    -- Convert to different time zones
    CONVERT_TZ(order_date, 'UTC', 'America/New_York') as est_time,
    CONVERT_TZ(order_date, 'UTC', 'America/Los_Angeles') as pst_time,
    CONVERT_TZ(order_date, 'UTC', 'Europe/London') as gmt_time,
    CONVERT_TZ(order_date, 'UTC', 'Asia/Tokyo') as jst_time,
    
    -- Business hours check across time zones
    CASE 
        WHEN HOUR(CONVERT_TZ(order_date, 'UTC', 'America/New_York')) BETWEEN 9 AND 17 
        THEN 'EST Business Hours'
        WHEN HOUR(CONVERT_TZ(order_date, 'UTC', 'Europe/London')) BETWEEN 9 AND 17 
        THEN 'GMT Business Hours'
        ELSE 'Outside Business Hours'
    END as business_hours_status
FROM orders
WHERE order_date >= '2024-01-01';
\`\`\`

## Advanced String Manipulation

### String Extraction and Parsing

\`\`\`sql
-- Complex string extraction
SELECT 
    customer_id,
    full_address,
    
    -- Extract address components
    SUBSTRING_INDEX(full_address, ',', 1) as street,
    TRIM(SUBSTRING_INDEX(SUBSTRING_INDEX(full_address, ',', 2), ',', -1)) as city,
    TRIM(SUBSTRING_INDEX(SUBSTRING_INDEX(full_address, ',', 3), ',', -1)) as state,
    TRIM(SUBSTRING_INDEX(full_address, ',', -1)) as zip_code,
    
    -- Email parsing
    email,
    SUBSTRING(email, 1, LOCATE('@', email) - 1) as username,
    SUBSTRING(email, LOCATE('@', email) + 1) as domain,
    SUBSTRING_INDEX(SUBSTRING(email, LOCATE('@', email) + 1), '.', -1) as tld,
    
    -- Phone number formatting
    phone,
    REGEXP_REPLACE(phone, '[^0-9]', '') as digits_only,
    CONCAT(
        '(',
        SUBSTRING(REGEXP_REPLACE(phone, '[^0-9]', ''), 1, 3),
        ') ',
        SUBSTRING(REGEXP_REPLACE(phone, '[^0-9]', ''), 4, 3),
        '-',
        SUBSTRING(REGEXP_REPLACE(phone, '[^0-9]', ''), 7, 4)
    ) as formatted_phone
FROM customers
WHERE full_address IS NOT NULL AND email IS NOT NULL;

-- Product code parsing
SELECT 
    product_id,
    sku,
    
    -- Extract SKU components (format: CAT-SUBCAT-123-SIZE)
    SUBSTRING_INDEX(sku, '-', 1) as category_code,
    SUBSTRING_INDEX(SUBSTRING_INDEX(sku, '-', 2), '-', -1) as subcategory_code,
    SUBSTRING_INDEX(SUBSTRING_INDEX(sku, '-', 3), '-', -1) as item_number,
    SUBSTRING_INDEX(sku, '-', -1) as size_code,
    
    -- Validate SKU format
    CASE 
        WHEN sku REGEXP '^[A-Z]{2,4}-[A-Z]{2,4}-[0-9]{3}-[A-Z0-9]{1,3}$' 
        THEN 'Valid Format'
        ELSE 'Invalid Format'
    END as sku_validation
FROM products
WHERE sku IS NOT NULL;
\`\`\`

### String Cleaning and Standardization

\`\`\`sql
-- Comprehensive string cleaning
SELECT 
    customer_id,
    first_name,
    last_name,
    
    -- Name standardization
    CONCAT(
        UPPER(LEFT(TRIM(first_name), 1)),
        LOWER(SUBSTRING(TRIM(first_name), 2))
    ) as clean_first_name,
    
    CONCAT(
        UPPER(LEFT(TRIM(last_name), 1)),
        LOWER(SUBSTRING(TRIM(last_name), 2))
    ) as clean_last_name,
    
    -- Remove extra spaces
    REGEXP_REPLACE(TRIM(CONCAT(first_name, ' ', last_name)), '[[:space:]]+', ' ') as full_name,
    
    -- Email cleaning
    LOWER(TRIM(email)) as clean_email,
    
    -- Address standardization
    REGEXP_REPLACE(
        REPLACE(
            REPLACE(
                REPLACE(
                    REPLACE(UPPER(TRIM(address)), 'STREET', 'ST'),
                    'AVENUE', 'AVE'
                ),
                'BOULEVARD', 'BLVD'
            ),
            'APARTMENT', 'APT'
        ),
        '[[:space:]]+', ' '
    ) as standardized_address,
    
    -- Company name cleaning
    TRIM(
        REPLACE(
            REPLACE(
                REPLACE(
                    REPLACE(company_name, 'Inc.', 'Inc'),
                    'Corporation', 'Corp'
                ),
                'Limited', 'Ltd'
            ),
            'Company', 'Co'
        )
    ) as clean_company_name
FROM customers;
\`\`\`

### Pattern Matching and Validation

\`\`\`sql
-- Advanced pattern matching for data validation
SELECT 
    customer_id,
    email,
    phone,
    credit_card_number,
    
    -- Email validation
    CASE 
        WHEN email REGEXP '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$' 
        THEN 'Valid Email'
        ELSE 'Invalid Email'
    END as email_status,
    
    -- Phone validation (US format)
    CASE 
        WHEN REGEXP_REPLACE(phone, '[^0-9]', '') REGEXP '^[0-9]{10}$' 
        THEN 'Valid Phone'
        ELSE 'Invalid Phone'
    END as phone_status,
    
    -- Credit card validation (basic format check)
    CASE 
        WHEN REGEXP_REPLACE(credit_card_number, '[^0-9]', '') REGEXP '^[0-9]{13,19}$' 
        THEN 'Valid Format'
        ELSE 'Invalid Format'
    END as card_format_status,
    
    -- Detect card type
    CASE 
        WHEN REGEXP_REPLACE(credit_card_number, '[^0-9]', '') REGEXP '^4[0-9]{12}([0-9]{3})?$' 
        THEN 'Visa'
        WHEN REGEXP_REPLACE(credit_card_number, '[^0-9]', '') REGEXP '^5[1-5][0-9]{14}$' 
        THEN 'MasterCard'
        WHEN REGEXP_REPLACE(credit_card_number, '[^0-9]', '') REGEXP '^3[47][0-9]{13}$' 
        THEN 'American Express'
        ELSE 'Unknown'
    END as card_type
FROM customers
WHERE email IS NOT NULL OR phone IS NOT NULL;

-- Text analysis and categorization
SELECT 
    product_id,
    product_name,
    description,
    
    -- Count words in description
    (LENGTH(description) - LENGTH(REPLACE(description, ' ', '')) + 1) as word_count,
    
    -- Extract key features from description
    CASE WHEN description REGEXP '([Ww]aterproof|[Ww]ater[- ]resistant)' THEN 1 ELSE 0 END as is_waterproof,
    CASE WHEN description REGEXP '([Bb]luetooth|[Ww]ireless)' THEN 1 ELSE 0 END as has_bluetooth,
    CASE WHEN description REGEXP '([Bb]attery|[Rr]echargeable)' THEN 1 ELSE 0 END as has_battery,
    CASE WHEN description REGEXP '([Ss]mart|[Ii]ntelligent|[Aa]I)' THEN 1 ELSE 0 END as is_smart_device,
    
    -- Extract measurements
    REGEXP_SUBSTR(description, '[0-9]+(\\.[0-9]+)?\\s*(inch|inches|in|cm|mm|feet|ft)') as dimensions,
    REGEXP_SUBSTR(description, '[0-9]+(\\.[0-9]+)?\\s*(lb|lbs|kg|gram|grams|oz)') as weight,
    
    -- Sentiment analysis keywords
    CASE 
        WHEN description REGEXP '(premium|luxury|high-quality|professional|advanced)' THEN 'Premium'
        WHEN description REGEXP '(budget|affordable|economical|basic|standard)' THEN 'Budget'
        WHEN description REGEXP '(innovative|cutting-edge|latest|new|modern)' THEN 'Innovative'
        ELSE 'Standard'
    END as product_positioning
FROM products
WHERE description IS NOT NULL;
\`\`\`

### Text Search and Analysis

\`\`\`sql
-- Full-text search simulation and text analysis
SELECT 
    product_id,
    product_name,
    description,
    category_name,
    
    -- Search relevance scoring
    (
        (CASE WHEN product_name LIKE '%smartphone%' THEN 10 ELSE 0 END) +
        (CASE WHEN description LIKE '%smartphone%' THEN 5 ELSE 0 END) +
        (CASE WHEN category_name LIKE '%phone%' THEN 8 ELSE 0 END) +
        (CASE WHEN product_name LIKE '%mobile%' THEN 8 ELSE 0 END) +
        (CASE WHEN description LIKE '%mobile%' THEN 3 ELSE 0 END)
    ) as search_relevance_score,
    
    -- Extract quoted text or features
    REGEXP_SUBSTR(description, '"[^"]*"') as quoted_feature,
    
    -- Find all numbers in description
    REGEXP_REPLACE(description, '[^0-9.]', ' ') as extracted_numbers,
    
    -- Calculate text similarity (simplified Jaccard similarity)
    LENGTH(description) as desc_length,
    (LENGTH(description) - LENGTH(REPLACE(LOWER(description), 'phone', ''))) / 5 as phone_mentions,
    (LENGTH(description) - LENGTH(REPLACE(LOWER(description), 'camera', ''))) / 6 as camera_mentions,
    (LENGTH(description) - LENGTH(REPLACE(LOWER(description), 'battery', ''))) / 7 as battery_mentions
FROM products p
JOIN categories c ON p.category_id = c.category_id
WHERE (
    product_name LIKE '%smartphone%' 
    OR description LIKE '%smartphone%'
    OR product_name LIKE '%mobile%'
    OR description LIKE '%mobile%'
)
ORDER BY search_relevance_score DESC;
\`\`\``,
    duration: '50 min',
    estimatedTime: 50,
    difficulty: 'intermediate',
    order: 5
  },
  {
    id: 'sql-intermediate-6',
    title: 'Data Modification Advanced Techniques',
    description: 'Master advanced INSERT, UPDATE, DELETE operations including bulk operations, conditional updates, and data synchronization.',
    content: `# Advanced Data Modification Techniques

## Advanced INSERT Operations

### Bulk INSERT with ON DUPLICATE KEY

\`\`\`sql
-- Insert or update existing records
INSERT INTO product_inventory (product_id, quantity, last_updated)
VALUES 
    (1, 100, NOW()),
    (2, 50, NOW()),
    (3, 75, NOW()),
    (4, 0, NOW())
ON DUPLICATE KEY UPDATE 
    quantity = VALUES(quantity),
    last_updated = VALUES(last_updated),
    update_count = COALESCE(update_count, 0) + 1;

-- Complex ON DUPLICATE KEY with conditional logic
INSERT INTO customer_statistics (customer_id, total_orders, total_spent, last_order_date)
SELECT 
    customer_id,
    COUNT(*) as total_orders,
    SUM(total_amount) as total_spent,
    MAX(order_date) as last_order_date
FROM orders
WHERE order_status = 'completed'
GROUP BY customer_id
ON DUPLICATE KEY UPDATE 
    total_orders = VALUES(total_orders),
    total_spent = VALUES(total_spent),
    last_order_date = GREATEST(last_order_date, VALUES(last_order_date)),
    updated_at = NOW();
\`\`\`

### INSERT with Complex SELECT

\`\`\`sql
-- Insert aggregated data into summary tables
INSERT INTO monthly_sales_summary (year, month, category_id, total_sales, order_count, avg_order_value)
SELECT 
    YEAR(o.order_date) as year,
    MONTH(o.order_date) as month,
    p.category_id,
    SUM(oi.quantity * oi.unit_price) as total_sales,
    COUNT(DISTINCT o.order_id) as order_count,
    AVG(o.total_amount) as avg_order_value
FROM orders o
JOIN order_items oi ON o.order_id = oi.order_id
JOIN products p ON oi.product_id = p.product_id
WHERE o.order_status = 'completed'
    AND o.order_date >= DATE_SUB(NOW(), INTERVAL 1 MONTH)
GROUP BY YEAR(o.order_date), MONTH(o.order_date), p.category_id
HAVING total_sales > 1000;

-- Insert with conditional logic
INSERT INTO customer_loyalty_tiers (customer_id, tier_level, tier_name, benefits, effective_date)
SELECT 
    customer_id,
    CASE 
        WHEN total_spent >= 10000 THEN 4
        WHEN total_spent >= 5000 THEN 3
        WHEN total_spent >= 1000 THEN 2
        ELSE 1
    END as tier_level,
    CASE 
        WHEN total_spent >= 10000 THEN 'Platinum'
        WHEN total_spent >= 5000 THEN 'Gold'
        WHEN total_spent >= 1000 THEN 'Silver'
        ELSE 'Bronze'
    END as tier_name,
    CASE 
        WHEN total_spent >= 10000 THEN JSON_ARRAY('Free shipping', 'Priority support', '15% discount', 'Exclusive products')
        WHEN total_spent >= 5000 THEN JSON_ARRAY('Free shipping', 'Priority support', '10% discount')
        WHEN total_spent >= 1000 THEN JSON_ARRAY('Free shipping', '5% discount')
        ELSE JSON_ARRAY('Standard support')
    END as benefits,
    NOW() as effective_date
FROM (
    SELECT 
        c.customer_id,
        COALESCE(SUM(o.total_amount), 0) as total_spent
    FROM customers c
    LEFT JOIN orders o ON c.customer_id = o.customer_id AND o.order_status = 'completed'
    WHERE c.is_active = TRUE
    GROUP BY c.customer_id
) customer_totals;
\`\`\`

### Multi-table INSERT (Simulated with Transactions)

\`\`\`sql
-- Complex multi-table insert with transaction
START TRANSACTION;

-- Insert order
INSERT INTO orders (customer_id, order_date, order_status, total_amount)
VALUES (123, NOW(), 'processing', 0);

SET @order_id = LAST_INSERT_ID();

-- Insert order items
INSERT INTO order_items (order_id, product_id, quantity, unit_price, total_price)
SELECT 
    @order_id,
    ci.product_id,
    ci.quantity,
    p.price,
    ci.quantity * p.price
FROM cart_items ci
JOIN products p ON ci.product_id = p.product_id
WHERE ci.customer_id = 123;

-- Update order total
UPDATE orders 
SET total_amount = (
    SELECT SUM(total_price) 
    FROM order_items 
    WHERE order_id = @order_id
)
WHERE order_id = @order_id;

-- Update product inventory
UPDATE products p
JOIN (
    SELECT product_id, SUM(quantity) as ordered_qty
    FROM order_items 
    WHERE order_id = @order_id
    GROUP BY product_id
) oi ON p.product_id = oi.product_id
SET p.stock_quantity = p.stock_quantity - oi.ordered_qty;

-- Clear cart
DELETE FROM cart_items WHERE customer_id = 123;

-- Log the transaction
INSERT INTO order_audit_log (order_id, action, details, created_at)
VALUES (@order_id, 'ORDER_CREATED', JSON_OBJECT('customer_id', 123, 'item_count', @@row_count), NOW());

COMMIT;
\`\`\`

## Advanced UPDATE Operations

### Conditional Updates with CASE

\`\`\`sql
-- Complex conditional pricing updates
UPDATE products 
SET 
    price = CASE 
        WHEN category_id = 1 AND stock_quantity > 100 THEN price * 0.9  -- Electronics discount
        WHEN category_id = 2 AND created_at < DATE_SUB(NOW(), INTERVAL 6 MONTH) THEN price * 0.8  -- Old books
        WHEN stock_quantity = 0 THEN price * 1.1  -- Out of stock markup
        WHEN stock_quantity < 10 THEN price * 1.05  -- Low stock markup
        ELSE price
    END,
    discount_percent = CASE 
        WHEN category_id = 1 AND stock_quantity > 100 THEN 10
        WHEN category_id = 2 AND created_at < DATE_SUB(NOW(), INTERVAL 6 MONTH) THEN 20
        ELSE 0
    END,
    updated_at = NOW()
WHERE is_active = TRUE;

-- Update with subquery calculations
UPDATE customers c
SET 
    loyalty_points = loyalty_points + (
        SELECT COALESCE(SUM(total_amount * 0.1), 0)
        FROM orders o
        WHERE o.customer_id = c.customer_id
            AND o.order_date >= DATE_SUB(NOW(), INTERVAL 30 DAY)
            AND o.order_status = 'completed'
    ),
    tier_status = CASE 
        WHEN (
            SELECT COALESCE(SUM(total_amount), 0)
            FROM orders o
            WHERE o.customer_id = c.customer_id
                AND o.order_status = 'completed'
        ) >= 10000 THEN 'Platinum'
        WHEN (
            SELECT COALESCE(SUM(total_amount), 0)
            FROM orders o
            WHERE o.customer_id = c.customer_id
                AND o.order_status = 'completed'
        ) >= 5000 THEN 'Gold'
        WHEN (
            SELECT COALESCE(SUM(total_amount), 0)
            FROM orders o
            WHERE o.customer_id = c.customer_id
                AND o.order_status = 'completed'
        ) >= 1000 THEN 'Silver'
        ELSE 'Bronze'
    END
WHERE c.is_active = TRUE;
\`\`\`

### Multi-table UPDATE with JOIN

\`\`\`sql
-- Update with JOIN operations
UPDATE products p
JOIN (
    SELECT 
        product_id,
        AVG(rating) as avg_rating,
        COUNT(*) as review_count
    FROM reviews
    WHERE created_at >= DATE_SUB(NOW(), INTERVAL 1 YEAR)
    GROUP BY product_id
) r ON p.product_id = r.product_id
JOIN categories c ON p.category_id = c.category_id
SET 
    p.average_rating = r.avg_rating,
    p.review_count = r.review_count,
    p.rating_category = CASE 
        WHEN r.avg_rating >= 4.5 THEN 'Excellent'
        WHEN r.avg_rating >= 4.0 THEN 'Very Good'
        WHEN r.avg_rating >= 3.5 THEN 'Good'
        WHEN r.avg_rating >= 3.0 THEN 'Average'
        ELSE 'Below Average'
    END,
    p.is_featured = CASE 
        WHEN r.avg_rating >= 4.5 AND r.review_count >= 20 THEN TRUE
        ELSE FALSE
    END,
    p.updated_at = NOW()
WHERE p.is_active = TRUE;

-- Cross-table inventory synchronization
UPDATE product_inventory pi
JOIN (
    SELECT 
        product_id,
        SUM(CASE WHEN transaction_type = 'sale' THEN -quantity ELSE quantity END) as net_change
    FROM inventory_transactions
    WHERE transaction_date >= DATE_SUB(NOW(), INTERVAL 1 DAY)
        AND processed = FALSE
    GROUP BY product_id
) it ON pi.product_id = it.product_id
SET 
    pi.quantity = pi.quantity + it.net_change,
    pi.last_updated = NOW(),
    pi.last_transaction_date = NOW()
WHERE pi.quantity + it.net_change >= 0;

-- Mark transactions as processed
UPDATE inventory_transactions
SET processed = TRUE
WHERE transaction_date >= DATE_SUB(NOW(), INTERVAL 1 DAY)
    AND processed = FALSE;
\`\`\`

### Incremental and Batch Updates

\`\`\`sql
-- Batch update with progress tracking
UPDATE order_status_updates 
SET status = 'processing', updated_at = NOW()
WHERE order_id IN (
    SELECT order_id FROM orders 
    WHERE order_status = 'pending' 
        AND order_date < DATE_SUB(NOW(), INTERVAL 1 HOUR)
        AND payment_status = 'confirmed'
    LIMIT 1000
);

-- Incremental data migration
UPDATE customers_new cn
JOIN customers_old co ON cn.email = co.email
SET 
    cn.preferences = COALESCE(cn.preferences, co.old_preferences),
    cn.communication_settings = JSON_MERGE_PATCH(
        COALESCE(cn.communication_settings, JSON_OBJECT()),
        JSON_OBJECT(
            'email_marketing', co.email_opt_in,
            'sms_marketing', co.sms_opt_in,
            'phone_marketing', co.phone_opt_in
        )
    ),
    cn.migrated_at = NOW()
WHERE cn.migrated_at IS NULL
LIMIT 5000;
\`\`\`

## Advanced DELETE Operations

### Conditional DELETE with Subqueries

\`\`\`sql
-- Delete orphaned records
DELETE FROM order_items 
WHERE order_id NOT IN (
    SELECT order_id FROM orders WHERE order_id IS NOT NULL
);

-- Delete old temporary data
DELETE FROM user_sessions 
WHERE last_activity < DATE_SUB(NOW(), INTERVAL 30 DAY)
    OR (
        expires_at < NOW() 
        AND session_type = 'temporary'
    );

-- Delete duplicate records (keeping the latest)
DELETE t1 FROM product_reviews t1
JOIN product_reviews t2 
WHERE t1.product_id = t2.product_id 
    AND t1.customer_id = t2.customer_id
    AND t1.created_at < t2.created_at;

-- Complex business logic deletion
DELETE FROM promotional_codes 
WHERE code_id IN (
    SELECT code_id FROM (
        SELECT 
            pc.code_id,
            pc.expiration_date,
            pc.usage_limit,
            COALESCE(usage_stats.times_used, 0) as times_used
        FROM promotional_codes pc
        LEFT JOIN (
            SELECT 
                promo_code_id,
                COUNT(*) as times_used
            FROM orders 
            WHERE promo_code_id IS NOT NULL
            GROUP BY promo_code_id
        ) usage_stats ON pc.code_id = usage_stats.promo_code_id
        WHERE pc.expiration_date < NOW()
            OR (pc.usage_limit > 0 AND COALESCE(usage_stats.times_used, 0) >= pc.usage_limit)
            OR (pc.is_active = FALSE AND pc.created_at < DATE_SUB(NOW(), INTERVAL 1 YEAR))
    ) expired_codes
);
\`\`\`

### Cascading DELETE Operations

\`\`\`sql
-- Manual cascading delete with transaction
START TRANSACTION;

-- Store order IDs to be deleted
CREATE TEMPORARY TABLE orders_to_delete AS
SELECT order_id 
FROM orders 
WHERE customer_id = 12345 
    AND order_status = 'cancelled'
    AND created_at < DATE_SUB(NOW(), INTERVAL 1 YEAR);

-- Delete related records in correct order
DELETE FROM order_shipping_tracking 
WHERE order_id IN (SELECT order_id FROM orders_to_delete);

DELETE FROM order_payment_transactions 
WHERE order_id IN (SELECT order_id FROM orders_to_delete);

DELETE FROM order_status_history 
WHERE order_id IN (SELECT order_id FROM orders_to_delete);

DELETE FROM order_items 
WHERE order_id IN (SELECT order_id FROM orders_to_delete);

-- Finally delete the orders
DELETE FROM orders 
WHERE order_id IN (SELECT order_id FROM orders_to_delete);

-- Log the deletion
INSERT INTO data_cleanup_log (action, affected_records, details, performed_at)
SELECT 
    'BULK_DELETE_CANCELLED_ORDERS',
    COUNT(*),
    JSON_OBJECT('customer_id', 12345, 'cutoff_date', DATE_SUB(NOW(), INTERVAL 1 YEAR)),
    NOW()
FROM orders_to_delete;

DROP TEMPORARY TABLE orders_to_delete;

COMMIT;
\`\`\`

### Archive and DELETE Pattern

\`\`\`sql
-- Archive old data before deletion
INSERT INTO orders_archive 
SELECT 
    *,
    NOW() as archived_at,
    'AUTO_ARCHIVE' as archive_reason
FROM orders 
WHERE order_date < DATE_SUB(NOW(), INTERVAL 2 YEAR)
    AND order_status IN ('completed', 'cancelled', 'refunded');

-- Archive related order items
INSERT INTO order_items_archive
SELECT 
    oi.*,
    NOW() as archived_at
FROM order_items oi
JOIN orders o ON oi.order_id = o.order_id
WHERE o.order_date < DATE_SUB(NOW(), INTERVAL 2 YEAR)
    AND o.order_status IN ('completed', 'cancelled', 'refunded');

-- Now safely delete the archived data
DELETE oi FROM order_items oi
JOIN orders o ON oi.order_id = o.order_id
WHERE o.order_date < DATE_SUB(NOW(), INTERVAL 2 YEAR)
    AND o.order_status IN ('completed', 'cancelled', 'refunded');

DELETE FROM orders 
WHERE order_date < DATE_SUB(NOW(), INTERVAL 2 YEAR)
    AND order_status IN ('completed', 'cancelled', 'refunded');
\`\`\`

## Data Synchronization and ETL Patterns

### UPSERT (Merge) Operations

\`\`\`sql
-- Comprehensive UPSERT pattern
INSERT INTO customer_daily_stats (
    customer_id, 
    stat_date, 
    orders_placed, 
    total_spent, 
    items_purchased,
    avg_order_value,
    first_order_time,
    last_order_time
)
SELECT 
    customer_id,
    DATE(order_date) as stat_date,
    COUNT(*) as orders_placed,
    SUM(total_amount) as total_spent,
    SUM((SELECT SUM(quantity) FROM order_items WHERE order_id = o.order_id)) as items_purchased,
    AVG(total_amount) as avg_order_value,
    MIN(order_date) as first_order_time,
    MAX(order_date) as last_order_time
FROM orders o
WHERE order_date >= CURDATE()
    AND order_status = 'completed'
GROUP BY customer_id, DATE(order_date)
ON DUPLICATE KEY UPDATE
    orders_placed = VALUES(orders_placed),
    total_spent = VALUES(total_spent),
    items_purchased = VALUES(items_purchased),
    avg_order_value = VALUES(avg_order_value),
    first_order_time = LEAST(first_order_time, VALUES(first_order_time)),
    last_order_time = GREATEST(last_order_time, VALUES(last_order_time)),
    updated_at = NOW();
\`\`\`

### Change Data Capture Pattern

\`\`\`sql
-- Capture changes for downstream systems
INSERT INTO product_change_log (
    product_id,
    change_type,
    old_values,
    new_values,
    changed_at,
    changed_by
)
SELECT 
    p.product_id,
    'PRICE_UPDATE' as change_type,
    JSON_OBJECT(
        'price', p.price,
        'discount_percent', p.discount_percent,
        'effective_date', p.price_effective_date
    ) as old_values,
    JSON_OBJECT(
        'price', new_pricing.new_price,
        'discount_percent', new_pricing.new_discount,
        'effective_date', NOW()
    ) as new_values,
    NOW() as changed_at,
    USER() as changed_by
FROM products p
JOIN (
    SELECT 
        product_id,
        price * 1.05 as new_price,
        CASE WHEN category_id = 1 THEN 10 ELSE 5 END as new_discount
    FROM products
    WHERE last_price_update < DATE_SUB(NOW(), INTERVAL 30 DAY)
) new_pricing ON p.product_id = new_pricing.product_id
WHERE p.is_active = TRUE;

-- Apply the actual changes
UPDATE products p
JOIN (
    SELECT 
        product_id,
        price * 1.05 as new_price,
        CASE WHEN category_id = 1 THEN 10 ELSE 5 END as new_discount
    FROM products
    WHERE last_price_update < DATE_SUB(NOW(), INTERVAL 30 DAY)
) new_pricing ON p.product_id = new_pricing.product_id
SET 
    p.price = new_pricing.new_price,
    p.discount_percent = new_pricing.new_discount,
    p.price_effective_date = NOW(),
    p.last_price_update = NOW()
WHERE p.is_active = TRUE;
\`\`\``,
    duration: '65 min',
    estimatedTime: 65,
    difficulty: 'intermediate',
    order: 6
  }
];
